/**
 * Canonical status display utility.
 *
 * Single source of truth for collapsing the five backend project/task
 * statuses (SCOPING/TASKING/TODO/IN_PROGRESS/DONE) into the three
 * user-facing display buckets, and for the label/icon/class each bucket
 * renders with. Centralizes what was previously duplicated between
 * `ProjectCard.getStatusIcon`/`getStatusColor` and `useProjectFiltering`'s
 * status maps.
 *
 * This module is pure data + helpers — no React state, no I/O, no JSX.
 * Consumers compose the icon element themselves from the `Icon` component
 * reference plus `iconClass`, e.g. `<Icon className={getStatusIconClass(status)} />`.
 */

import type { LucideIcon } from 'lucide-react';
import { CheckCircle2, Circle, Zap } from 'lucide-react';

import type { DisplayStatus, ProjectStatus, TaskStatus } from '~/types/api/enums';

/** Backward-compat alias for pre-existing consumers of `useProjectFiltering`'s `StatusChip`. */
export type StatusChip = DisplayStatus;

/** The three user-facing filter chips in display order. */
export const FILTER_CHIPS: StatusChip[] = ['TODO', 'IN_PROGRESS', 'DONE'];

/** Human-readable labels for each chip. */
export const CHIP_LABELS: Record<StatusChip, string> = {
  TODO: 'Todo',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
};

/** Maps each chip to the backend statuses it includes. */
export const CHIP_TO_STATUSES: Record<StatusChip, string[]> = {
  TODO: ['SCOPING', 'TASKING', 'TODO'],
  IN_PROGRESS: ['IN_PROGRESS'],
  DONE: ['DONE'],
};

/** Maps every backend status to its user-facing display label. */
export const STATUS_DISPLAY_LABEL: Record<string, string> = {
  SCOPING: 'Todo',
  TASKING: 'Todo',
  TODO: 'Todo',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
};

export interface StatusDisplayEntry {
  /** User-facing label, e.g. 'Todo' / 'In Progress' / 'Done'. */
  label: string;
  /** Lucide icon component reference — the caller renders `<Icon className={...} />`. */
  Icon: LucideIcon;
  /** Theme-token classes for the icon (size + color); never hex/rgb literals. */
  iconClass: string;
  /** Theme-token classes for a status chip/badge; never hex/rgb literals. */
  chipClass: string;
}

/**
 * Collapses any raw project/task status into one of the three user-facing
 * DisplayStatus buckets: SCOPING/TASKING/TODO all collapse to 'TODO'.
 *
 * An unrecognized status defaults to 'TODO' — an explicit, documented safe
 * default (not a swallowed error): an unexpected backend status should
 * degrade to the least alarming bucket rather than throw and break
 * rendering.
 *
 * @param status - A `ProjectStatus`, `TaskStatus`, or any raw status string
 * @returns The collapsed `DisplayStatus`
 *
 * @example
 * collapseStatus('SCOPING') // 'TODO'
 * collapseStatus('IN_PROGRESS') // 'IN_PROGRESS'
 * collapseStatus('some-unknown-status') // 'TODO'
 */
export function collapseStatus(status: ProjectStatus | TaskStatus | string): DisplayStatus {
  switch (status) {
    case 'IN_PROGRESS':
      return 'IN_PROGRESS';
    case 'DONE':
      return 'DONE';
    case 'SCOPING':
    case 'TASKING':
    case 'TODO':
      return 'TODO';
    default:
      return 'TODO';
  }
}

/**
 * Canonical status → {label, icon, classes} map, keyed by `DisplayStatus`.
 *
 * Icon set and classes are transcribed verbatim from
 * `ProjectCard.getStatusIcon`/`getStatusColor`
 * (`apps/web/src/pages/Projects/ProjectCard.tsx`) — the mockup-confirmed
 * canonical set (Circle/Zap/CheckCircle2). `TaskCard.tsx` currently uses a
 * different icon set (Clock/Activity); reconciling it is a follow-up, not
 * part of this change.
 */
export const STATUS_DISPLAY: Record<DisplayStatus, StatusDisplayEntry> = {
  TODO: {
    label: 'Todo',
    Icon: Circle,
    iconClass: 'w-4 h-4 text-foreground-600 shrink-0',
    chipClass:
      'bg-foreground-50 dark:bg-foreground-100 text-foreground-700 dark:text-foreground-600',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    Icon: Zap,
    iconClass: 'w-4 h-4 text-primary shrink-0',
    chipClass: 'bg-primary-100 dark:bg-primary-200 text-primary-700 dark:text-primary-800',
  },
  DONE: {
    label: 'Done',
    Icon: CheckCircle2,
    iconClass: 'w-4 h-4 text-success shrink-0',
    chipClass: 'bg-success-100 dark:bg-success-200 text-success-700 dark:text-success-800',
  },
};

/**
 * @param status - A `ProjectStatus`, `TaskStatus`, or any raw status string
 * @returns The chip/badge theme-token class string for the status's collapsed bucket
 */
export function getStatusChipClass(status: ProjectStatus | TaskStatus | string): string {
  return STATUS_DISPLAY[collapseStatus(status)].chipClass;
}

/**
 * @param status - A `ProjectStatus`, `TaskStatus`, or any raw status string
 * @returns The Lucide icon component reference for the status's collapsed bucket
 */
export function getStatusIcon(status: ProjectStatus | TaskStatus | string): LucideIcon {
  return STATUS_DISPLAY[collapseStatus(status)].Icon;
}

/**
 * @param status - A `ProjectStatus`, `TaskStatus`, or any raw status string
 * @returns The icon's theme-token class string for the status's collapsed bucket
 */
export function getStatusIconClass(status: ProjectStatus | TaskStatus | string): string {
  return STATUS_DISPLAY[collapseStatus(status)].iconClass;
}

/**
 * @param status - A `ProjectStatus`, `TaskStatus`, or any raw status string
 * @returns The user-facing label for the status's collapsed bucket
 */
export function getStatusLabel(status: ProjectStatus | TaskStatus | string): string {
  return STATUS_DISPLAY[collapseStatus(status)].label;
}
